import { useState } from "react";
import { Check, X } from "lucide-react";

const keysOf = (q) => (q.type === "matching" ? q.data.lefts : q.data.items);
const valuesOf = (q) => (q.type === "matching" ? q.data.rights.map((r) => ({ id: r.id, text: r.text })) : q.data.categories.map((c) => ({ id: c.id, text: c.name })));
const keyText = (k) => k.text;

const Choice = ({ values, value, locked, onPick, testId }) => (
  <div className="flex flex-wrap gap-2">
    {values.map((v) => (
      <button key={v.id} type="button" disabled={locked} onClick={() => onPick(v.id)} data-testid={`${testId}-${v.id}`}
        className={`px-3 py-2 rounded-lg border-2 text-base font-bold transition-colors duration-150 ${value === v.id ? "bg-[#FACC15] border-[#FACC15] text-[#05070D]" : "border-[rgba(100,116,139,0.6)] text-slate-200"} ${locked && value !== v.id ? "opacity-40" : ""}`}>
        {v.text}
      </button>
    ))}
  </div>
);

export const StructuredAnswer = ({ state, send, remaining }) => {
  const q = state.question;
  const sent = state.my_answer && typeof state.my_answer === "object" ? state.my_answer : null;
  const [mapping, setMapping] = useState({});
  const cur = sent || mapping;
  const locked = Boolean(sent) || remaining <= 0;
  const keys = keysOf(q);
  const values = valuesOf(q);
  const done = keys.every((k) => cur[k.id]);
  return (
    <div className="space-y-4" data-testid="student-structured">
      <p className="text-sm text-slate-400">{q.type === "matching" ? "Her ifadenin eşini seç." : "Her öğenin grubunu seç."}</p>
      {keys.map((k) => (
        <div key={k.id} className="rounded-xl border border-[rgba(71,85,105,0.45)] bg-white/5 p-3 space-y-2" data-testid={`structured-key-${k.id}`}>
          <p className="text-lg font-bold">{keyText(k)}</p>
          <Choice values={values} value={cur[k.id]} locked={locked} testId={`structured-${k.id}`} onPick={(v) => setMapping((m) => ({ ...m, [k.id]: v }))} />
        </div>
      ))}
      {!sent && (
        <button type="button" disabled={!done || locked} onClick={() => send({ type: "answer", mapping })} data-testid="structured-submit"
          className="w-full h-14 rounded-xl bg-[#FACC15] text-[#05070D] font-display text-xl font-bold disabled:opacity-40">
          Yanıtı gönder
        </button>
      )}
    </div>
  );
};

export const StructuredResult = ({ state, mine }) => {
  const q = state.question;
  const vals = Object.fromEntries(valuesOf(q).map((v) => [v.id, v.text]));
  const items = state.results.items || [];
  return (
    <div className="space-y-2" data-testid="student-structured-result">
      <p className="eyebrow">Doğru yanıtlar · sınıfta doğru bilenler</p>
      {items.map((it) => {
        const pick = mine?.mapping?.[it.id];
        const ok = pick === it.correct_id;
        return (
          <div key={it.id} className="flex items-start gap-2 text-sm rounded-lg bg-white/5 px-3 py-2">
            {mine?.mapping ? (ok ? <Check className="h-4 w-4 text-[#34D399] mt-0.5" /> : <X className="h-4 w-4 text-red-400 mt-0.5" />) : null}
            <span className="flex-1"><b>{it.label}</b> → <span className="text-[#6EE7B7]">{it.correct_label}</span>
              {pick && !ok && <span className="text-slate-400"> (senin: {vals[pick]})</span>}</span>
            <span className="font-mono-x">%{it.pct}</span>
          </div>
        );
      })}
    </div>
  );
};

export const StructuredBoard = ({ q }) => {
  if (q.type === "matching") {
    return (
      <div className="grid grid-cols-2 gap-[3vw]" data-testid="board-matching">
        {[q.data.lefts, q.data.rights].map((col, c) => (
          <div key={c} className="space-y-[1.4vh]">
            {col.map((x) => (
              <div key={x.id} className="rounded-2xl border-2 border-[#FACC15]/50 bg-[rgba(15,23,42,0.5)] px-[1.5vw] py-[1.6vh] text-[min(2.25rem,3.4vh)] font-bold">{x.text}</div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-[2.5vh]" data-testid="board-classification">
      <div className="flex flex-wrap gap-[1.2vw]">
        {q.data.items.map((i) => (
          <span key={i.id} className="rounded-xl border-2 border-slate-500/60 bg-white/5 px-[1.2vw] py-[1vh] text-[min(2rem,3.2vh)] font-bold">{i.text}</span>
        ))}
      </div>
      <div className="grid gap-[1.2vw]" style={{ gridTemplateColumns: `repeat(${q.data.categories.length}, minmax(0, 1fr))` }}>
        {q.data.categories.map((c) => (
          <div key={c.id} className="rounded-2xl border-2 border-dashed border-[#FACC15]/60 px-[1vw] py-[3vh] text-center font-display text-[min(2.4rem,3.8vh)] font-bold text-[#FDE047]">{c.name}</div>
        ))}
      </div>
    </div>
  );
};
